import "./HealthCheckPage.css";

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";

import {HealthCheckStatus, ManufacturerHealthCheckRespStatus, CatSize, Manufacturer, ManufacturerIntersection, UnexpectedKeywordRow, EmptyProdRow, SkippedProdsRow, BrokenUrlDoc, AnalysisType, ProdAttributeName, AnalysisTypeSelection} from "../types";
import {healthcheckService} from "../services/healthcheckService";
import ManufacturerSelect from "../components/ManufacturerInput";
import {BrokenUrlDocsTable, BrokenSubsUrlDocsTable, EmptyProdsTable, IntersectionsTable, SkippedProductsTable, UnexpectedKwTable, IntersectedAttrsTable} from "../components/FoldingTables";
import CheckboxPopup, {CheckboxPopupOption} from "../components/CheckboxPopup";

const normalizeName = (n: string) => n.replace(/[-+&()/]/g, ' ').replace(/\s+/g, ' ').replace(/ /g, '_');

const options: CheckboxPopupOption<AnalysisType, ProdAttributeName>[] = [
  { label: "Categories Intersections", value: "intersections" },
  { label: "Unexpected Keywords", value: "unexpected_kw" },
  { label: "Intersected Attributes", value: "intersected_attrs" },
  { label: "Empty Products", value: "empty_prods" },
  { label: "Skipped Products", value: "skipped_prods", subLabel: "Select keyword attributes (empty for default)", subOptions: ['collection', 'name', 'productNumber', 'imageFile', 'ColorName', 'ColorNumber', 'Backing', 'Size', 'Length', 'LengthM', 'Width', 'WidthM', 'Height', 'HeightM', 'specSheet', 'warrantySheet', 'maintenanceSheet', 'installationSheet', 'msdsSheet', 'Finish', 'WearLayerThickness', 'SurfaceTexture', 'productTypeId', 'URL', 'QuantityPerContainer', 'PiecesPerContainer', 'WeightPerContainer', 'Shape', 'SKU', 'FormatType', 'FlooringRadiantPanel', 'IndoorAirQuality', 'InstallationMethod', 'Material', 'PatternScale', 'Reclamation', 'tagsKeywords', 'WeldRod', 'imageColor', 'Volume', 'VolumeM', 'productId', 'Base', 'Coverage', 'CoatingType', 'IntendedUse', 'takeoffSize', 'Edge', 'Perforation', 'NRC', 'CAC', 'AC', 'SuspensionSystems', 'FirePerformance', 'LightReflectance', 'FaceProfile', 'ProfileType', 'ComponentType', 'ComponentDescription', 'DutyRating', 'Seismic', 'EndDetail', 'CBMBInterface', 'SlatsPerPanel', 'HumidityResistance', 'Pattern', 'FireResistance', 'mounting'] },
  { label: "Broken URLs", value: "broken_urls" },
  { label: "Broken subs URLs", value: "broken_subs_urls" },
];

const HealthCheckPage = () => {
    const [searchParams] = useSearchParams();
    const p_man_name = useMemo(() => searchParams.get('manufacturer') ?? "", [searchParams]);

    const [selectedManufacturerName, setSelectedManufacturerName] = useState<string>('');
    const [healthCheckStatus, setHealthCheckStatus] = useState<HealthCheckStatus>(HealthCheckStatus.NotLoaded);
    const availableManufacturers = useRef<Manufacturer[]>([]);
    const [fullListSize, setFullListSize] = useState<number>(0);
    const [fullListFname, setFullListFname] = useState<string>('');
    const [intersectionResult, setIntersectionResult] = useState<ManufacturerIntersection[]>([]);
    const [unexpectedKwResult, setUnexpectedKwResult] = useState<UnexpectedKeywordRow[]>([]);
    const [intersectedAttrsResult, setIntersectedAttrsResult] = useState<UnexpectedKeywordRow[]>([]);
    const [emptyProdsResult, setEmptyProdsResult] = useState<EmptyProdRow[]>([]);
    const [skippedProdsResult, setSkippedProdsResult] = useState<SkippedProdsRow[]>([]);
    const [brokenUrlDocResult, setBrokenUrlDocResult] = useState<BrokenUrlDoc[]>([]);
    const [brokenSubsUrlResult, setBrokenSubsUrlResult] = useState<BrokenUrlDoc[]>([]);
    const [categorySizes, setCategorySizes] = useState<CatSize[]>([]);
    const [lastUpdate, setLastUpdate] = useState<string>('');
    const [showRerunAnalysisPopup, setShowRerunAnalysisPopup] = useState(false);

    const handlePopupConfirm = (selected: AnalysisTypeSelection[]) => {
        healthcheckService.performHealthCheck(normalizeName(selectedManufacturerName), selected).then(resp => {
            if (resp.errorCode === undefined && resp.success) {
                setHealthCheckStatus(HealthCheckStatus.RunningHealthCheck);
                scheduleUpdateStatus(3000);
            } else {
                setHealthCheckStatus(HealthCheckStatus.Error);
            }
        });
        setShowRerunAnalysisPopup(false);
    };

    const handlePopupClose = () => {
        setShowRerunAnalysisPopup(false);
    };

    const navigate = useNavigate();

    const scheduleUpdateStatus = useCallback((delay: number) => {
        setTimeout(() => {
            healthcheckService.getManufacturersStatus(normalizeName(selectedManufacturerName)).then(resp => {
            if (resp.errorCode === undefined) {
                if (resp.success) {
                    setHealthCheckStatus(HealthCheckStatus.ManufacturerSelected);
                } else {
                    scheduleUpdateStatus(Math.min(delay*1.2, 20000));
                }
            } else {
                setHealthCheckStatus(HealthCheckStatus.Error);
            }
        });
        }, delay);
    }, [selectedManufacturerName, setHealthCheckStatus]);

    const runAnalysis = useCallback(() => {
        if (healthCheckStatus === HealthCheckStatus.HealthCheckComplete){
            setShowRerunAnalysisPopup(true);
        } else {
            navigate(`/manufacturer-analysis?manufacturer=${normalizeName(selectedManufacturerName)}`);
            setHealthCheckStatus(HealthCheckStatus.SelectingManufacturer);
        }
    }, [selectedManufacturerName, healthCheckStatus, setHealthCheckStatus, navigate]);

    useEffect(() => {
        if (healthCheckStatus === HealthCheckStatus.NotLoaded) {
            setHealthCheckStatus(HealthCheckStatus.ManufacturersLoading);
            healthcheckService.getManufacturers().then(resp => {
                if (resp.errorCode === undefined && resp.manufacturers) {
                    availableManufacturers.current = resp.manufacturers;
                    setHealthCheckStatus(HealthCheckStatus.ManufacturersLoaded);
                } else {
                    setHealthCheckStatus(HealthCheckStatus.Error);
                }
            });
        } else if (healthCheckStatus === HealthCheckStatus.ManufacturersLoaded && p_man_name) {
            // navigate('/manufacturer-analysis');
            let selected = false;
            for (let m of availableManufacturers.current) {
                if (normalizeName(m.manufacturer) === p_man_name) {
                    selected = true;
                    setSelectedManufacturerName(m.manufacturer);
                    setHealthCheckStatus(HealthCheckStatus.ManufacturerSelected);
                    // navigate('/manufacturer-analysis?manufacturer='+normalizeName(m.manufacturer));
                    break;
                }
            }
            if (!selected) {
                console.log(`No such manufacturer: '${p_man_name}'\navailable manufacturers: [${availableManufacturers.current.map(m => normalizeName(m.manufacturer)).join(', ')}]`);
                navigate('/manufacturer-analysis');
            }
        } else if (healthCheckStatus === HealthCheckStatus.ManufacturerSelected && p_man_name) {
            setHealthCheckStatus(HealthCheckStatus.HealthCheckResLoading);
              healthcheckService.getManufacturersHealthCheckResult(normalizeName(selectedManufacturerName)).then(resp => {
                if (resp.errorCode === undefined) {
                    if (resp.status === ManufacturerHealthCheckRespStatus.NotChecked) {
                        healthcheckService.performHealthCheck(normalizeName(selectedManufacturerName), undefined).then(resp2 => {
                            if (resp2.errorCode === undefined && resp2.success) {
                                setHealthCheckStatus(HealthCheckStatus.RunningHealthCheck);
                                scheduleUpdateStatus(3000);
                            } else {
                                setHealthCheckStatus(HealthCheckStatus.Error);
                            }
                        });
                    } else if (resp.status === ManufacturerHealthCheckRespStatus.StillInProgress) {
                        setHealthCheckStatus(HealthCheckStatus.RunningHealthCheck);
                        scheduleUpdateStatus(3000);
                    } else if (resp.status === ManufacturerHealthCheckRespStatus.IsReady) {
                        setCategorySizes(resp.cat_sizes);
                        setIntersectionResult(resp.intersections);
                        setUnexpectedKwResult(resp.unexpected_kw);
                        setIntersectedAttrsResult(resp.intersected_attrs);
                        setEmptyProdsResult(resp.empty_prods);
                        setSkippedProdsResult(resp.skipped_prods);
                        setBrokenUrlDocResult(resp.broken_urls);
                        setBrokenSubsUrlResult(resp.broken_subs_urls);
                        setFullListSize(resp.full_list);
                        setLastUpdate((new Date(resp.timestamp * 1000)).toISOString());
                        setHealthCheckStatus(HealthCheckStatus.HealthCheckComplete);
                    } else {
                        setHealthCheckStatus(HealthCheckStatus.Error);
                    }
                } else {
                    setHealthCheckStatus(HealthCheckStatus.Error);
                }
            });
        } else if (healthCheckStatus === HealthCheckStatus.SelectingManufacturer && !p_man_name) {
            setHealthCheckStatus(HealthCheckStatus.ManufacturerSelected);
        }
    }, [healthCheckStatus, scheduleUpdateStatus, setHealthCheckStatus, navigate, p_man_name, selectedManufacturerName]);

    return (
        <div className="excel-diff-page">
            {healthCheckStatus >= HealthCheckStatus.ManufacturersLoaded?
                <div className="inputs-panel">
                    <div className="inputs-container">
                        <ManufacturerSelect
                            manufacturers={availableManufacturers.current}
                            label="Select the manufacturer:"
                            value={selectedManufacturerName}
                            valueSetter={(v: string) => {
                                setHealthCheckStatus(HealthCheckStatus.SelectingManufacturer);
                                setSelectedManufacturerName(v);
                                navigate('/manufacturer-analysis');
                            }}
                        />
                    </div>
                    <div className="button-container">
                        <button className="button green-button"
                                disabled={selectedManufacturerName === '' || (healthCheckStatus !== HealthCheckStatus.ManufacturerSelected && healthCheckStatus !== HealthCheckStatus.HealthCheckComplete)}
                                onClick={runAnalysis}>{healthCheckStatus === HealthCheckStatus.HealthCheckComplete? "Rerun analysis" : "Analyze"}
                        </button>
                        {healthCheckStatus === HealthCheckStatus.HealthCheckComplete && (intersectionResult.length > 0 || unexpectedKwResult.length > 0 || intersectedAttrsResult.length > 0)?
                            <button className="button green-button"
                                    onClick={async () => {
                                        await healthcheckService.exportHealthCheckResult(normalizeName(selectedManufacturerName))
                                    }}>Export
                            </button> :
                        <></>}
                        {healthCheckStatus === HealthCheckStatus.HealthCheckComplete ?
                            <button className="button green-button"
                                    onClick={() => {
                                        alert("Manufacturer's categories:\n" + categorySizes.map(c => `${c.cat} -- ${c.rows} rows`).join('\n'))
                                    }}>Show cat sizes
                            </button> :
                        <></>}
                    </div>
                </div> :
                <></>}
            <div className="table-container scrollable">
                {healthCheckStatus === HealthCheckStatus.ManufacturersLoading ?
                    <p className="info-text">Loading manufacturers list. Please, be patient and don't refresh the
                        page</p> :
                    healthCheckStatus === HealthCheckStatus.RunningHealthCheck ?
                        <p className="info-text">Performing analysis. It may take up to 50 minutes. Please, be patient
                            and don't refresh the
                            page!</p> :
                        healthCheckStatus === HealthCheckStatus.HealthCheckResLoading ?
                    <p className="info-text">Loading results. Please, be patient and don't refresh the
                        page!</p> : <></>}
                {healthCheckStatus === HealthCheckStatus.Error ?
                    <p className="error-text">Network error occured! Please, reload the page and try
                        again...</p> : <></>}

                {healthCheckStatus === HealthCheckStatus.HealthCheckComplete ?
                    <>
                        <p className="info-text-mini" style={{marginTop: 2}}>{`Last check: ${lastUpdate.substring(0, 10)} at ${lastUpdate.substring(11, 19)} (now is ${(new Date()).toISOString().substring(11, 19)} of ${(new Date()).toISOString().substring(0, 10)}). Press "Rerun Analysis" if you want to refresh results.`}</p>
                        {intersectionResult.length === 0 && unexpectedKwResult.length === 0 && intersectedAttrsResult.length === 0?
                            <p className="info-text">Congratulations! No intersections and unexpected keywords found between this manufacturer's categories!</p> :
                            <></>
                        }{intersectionResult.length > 0?
                            <IntersectionsTable result={intersectionResult}/> : <></>
                        }{unexpectedKwResult.length > 0?
                            <UnexpectedKwTable result={unexpectedKwResult}/> : <></>
                        }{intersectedAttrsResult.length > 0?
                            <IntersectedAttrsTable result={intersectedAttrsResult}/> : <></>
                        }{emptyProdsResult.length > 0?
                            <EmptyProdsTable result={emptyProdsResult}/> : <></>
                        }{brokenUrlDocResult.length > 0?
                            <BrokenUrlDocsTable result={brokenUrlDocResult}/> : <></>
                        }{brokenSubsUrlResult.length > 0?
                            <BrokenSubsUrlDocsTable result={brokenSubsUrlResult}/> : <></>
                        }
                        <SkippedProductsTable manNameN={normalizeName(selectedManufacturerName)} currFileName={fullListFname} setCurrFileName={setFullListFname} currFileLen={fullListSize} setCurrFileLen={setFullListSize} result={skippedProdsResult}/>
                    </> :
                    <></>
                }
            </div>
            {showRerunAnalysisPopup && (
                <CheckboxPopup
                    options={options}
                    onConfirm={handlePopupConfirm}
                    onCancel={handlePopupClose}
                    title="Choose the metrics to reanalyze"
                />
            )}
        </div>
    );
}

export default HealthCheckPage;
